import { ArrowLeft, Pencil, Send } from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';
import { useActionsCourrier, useConseillers, useCourrier, useDestinataires, type InstructionSaisie } from '@/api/hooks';
import { LIBELLE_PRIORITE, LIBELLE_TYPE_DOCUMENT, LIBELLE_TYPE_INSTRUCTION, type Priorite } from '@/api/types';
import { Chargement, ConfirmationDialog, ErreurBloc, PageHeader } from '@/components/common';
import { PiecesJointes } from '@/components/pieces-jointes';
import { HistoriqueListe, InstructionsTable, PrioriteBadge, StatutCourrierBadge } from '@/components/courrier';
import { InstructionsBuilder, instructionVide } from '@/components/instructions-builder';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { SelectNatif } from '@/components/ui/select-natif';
import { Textarea } from '@/components/ui/textarea';
import { dateFr, dateHeureFr } from '@/lib/utils';

type ActionDecision = 'TRANSMETTRE' | 'RENVOYER' | 'CLASSER' | 'CONFIDENTIEL';

export function CourrierDetail() {
  const { id } = useParams();
  const { data, isLoading, error } = useCourrier(id);
  const actions = useActionsCourrier();
  const conseillers = useConseillers();
  const destinataires = useDestinataires();
  const [dialogue, setDialogue] = useState<'transmettre' | 'soumettre' | 'decision' | 'cloturer' | null>(null);
  // Analyse du conseiller technique
  const [analyse, setAnalyse] = useState({ texte: '', avis: '' });
  const [orientation, setOrientation] = useState<InstructionSaisie[]>([]);
  // Transmission pour analyse
  const [conseillerId, setConseillerId] = useState('');
  const [motifReorientation, setMotifReorientation] = useState('');
  // Décision du DG
  const [decision, setDecision] = useState<{ action: ActionDecision; annotation: string; priorite: Priorite; motif: string; instructions: InstructionSaisie[] }>({ action: 'TRANSMETTRE', annotation: '', priorite: 'NORMALE', motif: '', instructions: [instructionVide()] });

  if (isLoading) return <Chargement />;
  if (error || !data) return <ErreurBloc erreur={error} />;
  const { courrier: c, actions: a } = data;
  const nomDest = (agentId: string) => destinataires.data?.find((d) => d.agentId === agentId)?.nom ?? agentId;
  const executer = async (fn: () => Promise<unknown>, succes: string) => {
    try {
      await fn();
      toast.success(succes);
      setDialogue(null);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Action impossible.');
    }
  };
  const ouvrirDecision = () => {
    setDecision({ action: 'TRANSMETTRE', annotation: '', priorite: c.priorite, motif: '', instructions: c.analyse?.orientation.length ? c.analyse.orientation.map((o) => ({ responsableId: o.responsableId, type: o.type, texte: o.texte, echeance: o.echeance ?? undefined })) : [instructionVide()] });
    setDialogue('decision');
  };
  const reprendreProposition = () => {
    if (c.analyse?.orientation.length) setDecision((d) => ({ ...d, instructions: c.analyse!.orientation.map((o) => ({ responsableId: o.responsableId, type: o.type, texte: o.texte, echeance: o.echeance ?? undefined })) }));
  };
  const enregistrerAnalyse = () =>
    executer(
      () => actions.analyser.mutateAsync({ id: c.id, texte: analyse.texte.trim(), avis: analyse.avis.trim() || undefined, orientation: orientation.filter((o) => o.responsableId && o.texte.trim()).map((o) => ({ ...o, texte: o.texte.trim() })) }),
      'Analyse enregistrée. Transmettez au DG quand elle est prête.',
    );
  const decider = () =>
    executer(
      () =>
        actions.decider.mutateAsync({
          id: c.id,
          action: decision.action,
          annotation: decision.annotation.trim() || undefined,
          priorite: decision.priorite,
          motif: decision.motif.trim() || undefined,
          instructions: decision.action === 'TRANSMETTRE' ? decision.instructions.filter((i) => i.responsableId && i.texte.trim()).map((i) => ({ ...i, texte: i.texte.trim() })) : undefined,
        }),
      decision.action === 'TRANSMETTRE' ? 'Instructions transmises aux destinataires.' : 'Décision enregistrée.',
    );

  return (
    <div className="space-y-6">
      <PageHeader
        retour={
          <Link to="/courrier" className="mb-1 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="size-4" /> Courrier entrant
          </Link>
        }
        titre={
          <span className="flex flex-wrap items-center gap-2">
            {c.numero} <StatutCourrierBadge statut={c.statut} /> <PrioriteBadge priorite={c.priorite} />
            {c.confidentiel && <Badge variant="destructive">Confidentiel</Badge>}
          </span>
        }
        sousTitre={
          <>
            {c.objet} · {c.expediteur} · reçu le {dateFr(c.dateReception)} · {LIBELLE_TYPE_DOCUMENT[c.typeDocument]}
          </>
        }
        actions={
          <>
            {a.modifier && (
              <Button variant="outline" asChild>
                <Link to={`/courrier/${c.id}/modifier`}>
                  <Pencil className="size-4" /> Modifier
                </Link>
              </Button>
            )}
            {a.transmettreAnalyse && (
              <Button variant="outline" onClick={() => { setConseillerId(c.conseillerId ?? ''); setMotifReorientation(''); setDialogue('transmettre'); }}>
                {c.statut === 'ANALYSE' ? 'Réorienter' : 'Transmettre pour analyse'}
              </Button>
            )}
            {a.soumettre && (
              <Button onClick={() => setDialogue('soumettre')}>
                <Send className="size-4" /> Soumettre au DG
              </Button>
            )}
            {a.decider && <Button onClick={ouvrirDecision}>Décider</Button>}
            {a.cloturer && c.statut !== 'SOUMIS' && (
              <Button variant="outline" onClick={() => setDialogue('cloturer')}>
                Clôturer
              </Button>
            )}
          </>
        }
      />

      {data.doublons.length > 0 && (
        <Alert>
          <AlertTitle>Doublon possible</AlertTitle>
          <AlertDescription>
            Même expéditeur et même référence ou objet dans les 30 jours :{' '}
            {data.doublons.map((d) => (
              <Link key={d.id} to={`/courrier/${d.id}`} className="mr-2 text-primary hover:underline">
                {d.numero} ({dateFr(d.dateReception)})
              </Link>
            ))}
          </AlertDescription>
        </Alert>
      )}
      {data.delaiAnalyse?.depasse && (
        <Alert variant="destructive">
          <AlertTitle>Délai d'analyse dépassé</AlertTitle>
          <AlertDescription>Délai cible : {data.delaiAnalyse.jours} jour(s) pour cette priorité. L'assistante peut soumettre le courrier au DG sans avis.</AlertDescription>
        </Alert>
      )}

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Document</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 text-sm sm:grid-cols-2">
              <div><span className="text-muted-foreground">Expéditeur</span><div>{c.expediteur}</div></div>
              <div><span className="text-muted-foreground">Référence de l'expéditeur</span><div>{c.referenceExpediteur ?? '—'}</div></div>
              <div><span className="text-muted-foreground">Date du courrier</span><div>{dateFr(c.dateCourrier)}</div></div>
              <div><span className="text-muted-foreground">Enregistré par</span><div>{c.enregistrePar ? `${c.enregistrePar.prenom} ${c.enregistrePar.nom}` : '—'} · {dateHeureFr(c.createdAt)}</div></div>
              <div><span className="text-muted-foreground">Conseiller technique</span><div>{c.conseiller ? `${c.conseiller.prenom} ${c.conseiller.nom}` : 'À désigner'}</div></div>
              {(c.motsCles?.length ?? 0) > 0 && (
                <div className="sm:col-span-2">
                  <span className="text-muted-foreground">Mots-clés</span>
                  <div className="flex flex-wrap gap-1 pt-1">
                    {c.motsCles!.map((m) => (
                      <Badge key={m} variant="secondary">{m}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Analyse du conseiller technique</CardTitle>
              {c.analyse && <CardDescription>Rédigée le {dateHeureFr(c.analyse.date)}</CardDescription>}
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              {c.analyse && (
                <>
                  <div><div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Analyse</div><p className="whitespace-pre-line">{c.analyse.texte}</p></div>
                  {c.analyse.avis && <div><div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Avis et recommandations</div><p className="whitespace-pre-line">{c.analyse.avis}</p></div>}
                  {c.analyse.orientation.length > 0 && (
                    <div>
                      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Projet d'orientation</div>
                      <ul className="list-disc pl-5">
                        {c.analyse.orientation.map((o, i) => (
                          <li key={i}>
                            {nomDest(o.responsableId)} · {LIBELLE_TYPE_INSTRUCTION[o.type]} · {o.texte}
                            {o.echeance ? ` · échéance ${dateFr(o.echeance)}` : ''}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </>
              )}
              {a.analyser && (
                <div className="space-y-3 rounded-lg border p-3">
                  <div className="space-y-1.5">
                    <Label>Analyse (obligatoire, 20 caractères au moins)</Label>
                    <Textarea rows={4} value={analyse.texte} onChange={(e) => setAnalyse({ ...analyse, texte: e.target.value })} placeholder={c.analyse?.texte ?? ''} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Avis et recommandations</Label>
                    <Textarea rows={3} value={analyse.avis} onChange={(e) => setAnalyse({ ...analyse, avis: e.target.value })} placeholder={c.analyse?.avis ?? ''} />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Projet d'orientation (destinataires proposés au DG)</Label>
                    <InstructionsBuilder valeur={orientation} onChange={setOrientation} />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={enregistrerAnalyse} disabled={analyse.texte.trim().length < 20 || actions.analyser.isPending}>
                      Enregistrer l'analyse
                    </Button>
                    {a.soumettre && (
                      <Button onClick={() => setDialogue('soumettre')} disabled={!c.analyse}>
                        <Send className="size-4" /> Transmettre au DG
                      </Button>
                    )}
                  </div>
                </div>
              )}
              {!c.analyse && !a.analyser && <p className="text-muted-foreground">Aucune analyse.</p>}
            </CardContent>
          </Card>

          {c.annotation && (
            <Card>
              <CardHeader>
                <CardTitle>Annotation du Directeur Général</CardTitle>
                <CardDescription>{dateHeureFr(c.dateDecision)}</CardDescription>
              </CardHeader>
              <CardContent>
                <p className="whitespace-pre-line text-sm">{c.annotation}</p>
              </CardContent>
            </Card>
          )}
          {c.motifCloture && c.statut === 'CLOTURE' && (
            <Alert>
              <AlertTitle>Clôturé le {dateHeureFr(c.dateCloture)}</AlertTitle>
              <AlertDescription>{c.motifCloture}</AlertDescription>
            </Alert>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Instructions ({data.instructions.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <InstructionsTable instructions={data.instructions} document={false} vide="Aucune instruction pour ce courrier." />
            </CardContent>
          </Card>
          <PiecesJointes objetType="CE" objetId={c.id} typesSuggeres={['DOCUMENT_PRINCIPAL', 'COURRIER', 'RAPPORT', 'AUTRE']} description="Document principal numérisé obligatoire avant la soumission au DG ; annexes et avis facultatifs." />
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Historique</CardTitle>
            </CardHeader>
            <CardContent>
              <HistoriqueListe historique={c.historique} />
            </CardContent>
          </Card>
        </div>
      </div>

      <Dialog open={dialogue === 'transmettre'} onOpenChange={(o) => !o && setDialogue(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{c.statut === 'ANALYSE' ? 'Réorienter vers un autre conseiller' : 'Transmettre pour analyse'}</DialogTitle>
            <DialogDescription>Le conseiller technique reçoit le courrier, rédige son analyse et le transmet au DG.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Conseiller technique</Label>
              <SelectNatif value={conseillerId} onChange={(e) => setConseillerId(e.target.value)}>
                <option value="">Choisir</option>
                {(conseillers.data ?? []).map((x) => (
                  <option key={x.agentId} value={x.agentId}>
                    {x.nom} · {x.fonction}
                  </option>
                ))}
              </SelectNatif>
            </div>
            {c.statut === 'ANALYSE' && (
              <div className="space-y-1.5">
                <Label>Motif de la réorientation (obligatoire)</Label>
                <Textarea rows={2} value={motifReorientation} onChange={(e) => setMotifReorientation(e.target.value)} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogue(null)}>Fermer</Button>
            <Button disabled={!conseillerId || (c.statut === 'ANALYSE' && !motifReorientation.trim()) || actions.transmettreAnalyse.isPending} onClick={() => executer(() => actions.transmettreAnalyse.mutateAsync({ id: c.id, conseillerId, motif: motifReorientation.trim() || undefined }), 'Courrier transmis pour analyse.')}>
              Transmettre
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        ouvert={dialogue === 'soumettre'}
        onOpenChange={(o) => setDialogue(o ? 'soumettre' : null)}
        titre="Soumettre au Directeur Général"
        description={c.analyse ? "Le courrier entre dans le parapheur du DG avec l'avis du conseiller technique." : 'Le courrier entre dans le parapheur du DG sans analyse ; ce choix est tracé.'}
        libelle="Soumettre"
        onConfirmer={() => executer(() => actions.soumettre.mutateAsync(c.id), 'Courrier soumis au DG.')}
        chargement={actions.soumettre.isPending}
      />
      <ConfirmationDialog
        ouvert={dialogue === 'cloturer'}
        onOpenChange={(o) => setDialogue(o ? 'cloturer' : null)}
        titre="Clôturer le courrier"
        description="Classement manuel ; les instructions ouvertes restent au registre."
        libelle="Clôturer"
        onConfirmer={(m) => (m.trim().length < 5 ? Promise.resolve(toast.error('Le motif est obligatoire.')) : executer(() => actions.cloturer.mutateAsync({ id: c.id, motif: m.trim() }), 'Courrier clôturé.'))}
        chargement={actions.cloturer.isPending}
        champ={{ label: 'Motif (obligatoire)' }}
      />

      <Dialog open={dialogue === 'decision'} onOpenChange={(o) => !o && setDialogue(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Décision du Directeur Général · {c.numero}</DialogTitle>
            <DialogDescription>L'annotation est apposée au document et enregistrée ; chaque destinataire reçoit sa propre instruction, inscrite au registre.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              {(['TRANSMETTRE', 'RENVOYER', 'CLASSER', 'CONFIDENTIEL'] as ActionDecision[]).map((x) => (
                <Button key={x} type="button" size="sm" variant={decision.action === x ? 'default' : 'outline'} onClick={() => setDecision({ ...decision, action: x })}>
                  {{ TRANSMETTRE: 'Transmettre avec instructions', RENVOYER: 'Renvoyer au conseiller', CLASSER: 'Classer sans suite', CONFIDENTIEL: 'Marquer confidentiel' }[x]}
                </Button>
              ))}
            </div>
            <div className="grid gap-3 sm:grid-cols-[1fr_12rem]">
              <div className="space-y-1.5">
                <Label>Annotation</Label>
                <Textarea rows={3} value={decision.annotation} onChange={(e) => setDecision({ ...decision, annotation: e.target.value })} placeholder="Texte apposé sur le document (instructions types ou texte libre)." />
              </div>
              <div className="space-y-1.5">
                <Label>Priorité</Label>
                <SelectNatif value={decision.priorite} onChange={(e) => setDecision({ ...decision, priorite: e.target.value as Priorite })}>
                  {(Object.keys(LIBELLE_PRIORITE) as Priorite[]).map((p) => (
                    <option key={p} value={p}>
                      {LIBELLE_PRIORITE[p]}
                    </option>
                  ))}
                </SelectNatif>
              </div>
            </div>
            {decision.action === 'TRANSMETTRE' && (
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Destinataires et instructions</Label>
                  {c.analyse?.orientation.length ? (
                    <Button type="button" variant="ghost" size="sm" onClick={reprendreProposition}>
                      Reprendre la proposition du conseiller
                    </Button>
                  ) : null}
                </div>
                <InstructionsBuilder valeur={decision.instructions} onChange={(instructions) => setDecision({ ...decision, instructions })} />
              </div>
            )}
            {(decision.action === 'RENVOYER' || decision.action === 'CLASSER') && (
              <div className="space-y-1.5">
                <Label>Motif (obligatoire)</Label>
                <Textarea rows={2} value={decision.motif} onChange={(e) => setDecision({ ...decision, motif: e.target.value })} />
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogue(null)}>Fermer</Button>
            <Button
              onClick={decider}
              disabled={actions.decider.isPending || ((decision.action === 'RENVOYER' || decision.action === 'CLASSER') && !decision.motif.trim()) || (decision.action === 'TRANSMETTRE' && !decision.instructions.some((i) => i.responsableId && i.texte.trim()))}
              variant={decision.action === 'CLASSER' ? 'destructive' : 'default'}
            >
              {{ TRANSMETTRE: 'Transmettre', RENVOYER: 'Renvoyer', CLASSER: 'Classer sans suite', CONFIDENTIEL: 'Marquer confidentiel' }[decision.action]}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
